/**
 * « Se situer » — le test de positionnement, vu de l'enfant.
 *
 * L'algorithme existe depuis le premier jour (`mastery` §8) et n'avait jamais
 * été appelé : aucun positionnement n'a jamais eu lieu, pour ni l'un ni l'autre
 * enfant, et θ partait donc de 1 500 pour tout le monde — la valeur qu'on pose
 * quand on ne sait rien. Cet écran est la moitié manquante ; sans lui, les deux
 * routes ajoutées côté serveur auraient reproduit exactement le défaut qu'on
 * était en train de refermer.
 *
 * **Aucun résultat n'est affiché, ni pendant, ni à la fin** (§8.3, invariant
 * §14.1). Ce que l'écran montre est un compte de questions posées sur un budget
 * — de quoi savoir qu'on avance et combien il reste — et jamais combien ont été
 * réussies. Le test se termine d'ailleurs *toujours* sur un item que l'enfant
 * réussit, et celui-là n'est pas compté : la dernière chose qu'on emporte d'un
 * positionnement doit être « je sais faire », pas un score.
 *
 * **Une seule route pour démarrer et pour continuer.** L'écran ne porte pas
 * l'état de l'escalier : il demande l'item suivant, et le serveur sait à quelle
 * marche il en est. Un client qui porterait la difficulté choisirait sa propre
 * marche, ce qui est très exactement ce qu'un test adaptatif interdit.
 *
 * L'exercice et sa correction viennent d'`ExerciceEtCorrige`, comme partout
 * ailleurs : la réponse repart par la route de tentative ordinaire, et c'est le
 * serveur qui décide qu'elle appartient à un positionnement.
 */

import { useCallback, useEffect, useState } from "react";

import { call } from "@/api/client";
import type { EtapeDePositionnement, ExerciseOut } from "@/api/schema";
import { ExerciceEtCorrige } from "@/child/ExerciceEtCorrige";
import { etiquette } from "@/ui/matieres";
import { Button, Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";

type Etat =
  | { quoi: "chargement" }
  | { quoi: "panne" }
  | { quoi: "matieres"; etapes: EtapeDePositionnement[] }
  | { quoi: "question"; etape: EtapeDePositionnement; item: ExerciseOut }
  | { quoi: "fini"; matiere: string }
  // Le pool peut ne rien avoir à la hauteur demandée. Ce n'est pas une panne et
  // ce n'est pas une fin : le test reste ouvert, il reprendra là où il en est.
  | { quoi: "vide"; matiere: string };

export function PositionnementScreen() {
  const [etat, setEtat] = useState<Etat>({ quoi: "chargement" });

  const charger = useCallback(async () => {
    setEtat({ quoi: "chargement" });
    try {
      const reponse = await call("positionnementEtat");
      setEtat({ quoi: "matieres", etapes: reponse.matieres ?? [] });
    } catch {
      setEtat({ quoi: "panne" });
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  const continuer = useCallback(async (matiere: string) => {
    setEtat({ quoi: "chargement" });
    try {
      const etape = await call("positionnementSuivant", { params: { matiere } });
      if (etape.termine) setEtat({ quoi: "fini", matiere });
      else if (etape.exercice) setEtat({ quoi: "question", etape, item: etape.exercice });
      else setEtat({ quoi: "vide", matiere });
    } catch {
      setEtat({ quoi: "panne" });
    }
  }, []);

  if (etat.quoi === "chargement") return <Loading label="Je prépare…" />;

  if (etat.quoi === "panne") {
    return (
      <Screen title="Se situer">
        <ErrorNote>Je n'arrive pas à préparer ce test.</ErrorNote>
        <Button onClick={() => void charger()}>Réessayer</Button>
      </Screen>
    );
  }

  if (etat.quoi === "fini") {
    return (
      <Screen title={etiquette(etat.matiere)}>
        {/* **Rien sur le niveau, et ce n'est pas une omission polie.** Ce que
            l'enfant emporte d'un positionnement est la suite : des exercices à
            sa hauteur. Lui annoncer un résultat, même en douceur, ferait de ces
            vingt minutes un examen — et transformerait le prochain en enjeu. */}
        <Empty>
          C'est fait. Il n'y a rien à retenir de ce test : il me sert, à moi, pour te proposer des
          exercices ni trop faciles ni trop durs.
        </Empty>
        <Button onClick={() => void charger()}>← Revenir aux matières</Button>
      </Screen>
    );
  }

  if (etat.quoi === "vide") {
    return (
      <Screen title={etiquette(etat.matiere)}>
        <Empty>Je n'ai pas de question à te poser pour l'instant. On reprendra plus tard.</Empty>
        <Button onClick={() => void charger()}>← Revenir aux matières</Button>
      </Screen>
    );
  }

  if (etat.quoi === "question") {
    const { etape, item } = etat;
    return (
      <Screen title={etiquette(etape.matiere)} lead={avancement(etape)}>
        <ExerciceEtCorrige
          item={item}
          matiere={etape.matiere}
          retour={() => void charger()}
          suivant={() => void continuer(etape.matiere)}
          libelleRetour="← Revenir aux matières"
          libelleSuivant="Question suivante"
          libelleChanger="Arrêter pour l'instant"
          papier={false}
        />
      </Screen>
    );
  }

  return (
    <Screen
      title="Se situer"
      lead="Quelques questions par matière, pour que je sache où tu en es. Ce n'est pas noté, et personne ne verra de résultat — pas même toi."
    >
      {etat.etapes.length === 0 ? (
        <Empty>Rien à situer pour le moment.</Empty>
      ) : (
        <ul className="revision__liste">
          {etat.etapes.map((etape) => (
            <li key={etape.matiere}>
              <button
                type="button"
                className="revision__item"
                disabled={etape.termine === true}
                onClick={() => void continuer(etape.matiere)}
              >
                <span className="revision__matiere">{etiquette(etape.matiere)}</span>
                <span className="revision__libelle">{invite(etape)}</span>
                <span className="revision__reste">{avancement(etape)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <Card title="Combien de temps">
        <p className="muted small">
          Une quinzaine de minutes par matière, et tu peux t'arrêter en route : ce qui est répondu
          est gardé, tu reprendras à la question suivante.
        </p>
      </Card>
    </Screen>
  );
}

/** Ce que propose une matière. Exporté pour être testé seul. */
export function invite(etape: EtapeDePositionnement): string {
  if (etape.termine) return "C'est fait.";
  if ((etape.poses ?? 0) > 0) return "Reprendre là où tu t'es arrêté.";
  return "Commencer.";
}

/**
 * L'avancement, et rien d'autre.
 *
 * Un compte de questions **posées**, jamais de réussites : c'est la seule
 * quantité que cet écran a le droit d'afficher (§8.3). La distinction n'est pas
 * théorique — « 7 sur 12 » ressemble assez à une note pour qu'un enfant la lise
 * comme telle si le mot « question » disparaît de la phrase.
 */
export function avancement(etape: EtapeDePositionnement): string {
  if (etape.termine) return "C'est fait";
  const poses = etape.poses ?? 0;
  const budget = etape.budget ?? 0;
  if (poses === 0) return budget > 0 ? `~${budget} questions` : "À faire";
  return `question ${poses + 1}${budget > 0 ? ` sur ~${budget}` : ""}`;
}
